import type { EnhancedDeal, ProjectOnboarding, ProjectTask, TaskGroup, TimberProject } from '../types';
import { compareTimberProjectsByDealMap } from './projectNumberSort';
import { projectHasActiveWorkQueueInvolvement } from './archEngineerWorkQueueEligibility';

export const ARCH_ENGINEER_WORK_QUEUE_DOC_ID = 'arch_engineer_work_queue' as const;

/** Parse Firestore settings doc into a uid → ordered project ids map. */
export function parseQueuesByUserIdFromFirestore(data: unknown): Record<string, string[]> {
  if (!data || typeof data !== 'object') return {};
  const rec = data as Record<string, unknown>;
  const raw = rec.queuesByUserId;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string[]> = {};
  for (const [uid, arr] of Object.entries(raw as Record<string, unknown>)) {
    if (!uid || !Array.isArray(arr)) continue;
    out[uid] = arr.filter((x): x is string => typeof x === 'string');
  }
  return out;
}

/** Lower index in queue = higher priority; projects not listed sort after, by project number. */
export function compareProjectsByWorkQueueThenNumber(
  a: TimberProject,
  b: TimberProject,
  queueIds: string[],
  dealsById: Map<string, EnhancedDeal>,
): number {
  const ia = a.id ? queueIds.indexOf(a.id) : -1;
  const ib = b.id ? queueIds.indexOf(b.id) : -1;
  const ra = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
  const rb = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
  if (ra !== rb) return ra - rb;
  return compareTimberProjectsByDealMap(a, b, dealsById);
}

export function sortProjectsByWorkQueueThenNumber(
  projects: TimberProject[],
  queueIds: string[],
  dealsById: Map<string, EnhancedDeal>,
): TimberProject[] {
  return [...projects].sort((a, b) => compareProjectsByWorkQueueThenNumber(a, b, queueIds, dealsById));
}

/**
 * Same ordered project list as Project Hub's architect/engineer work queue modal:
 * projects with active work-queue involvement for `userId`, ordered by saved queue then project number.
 */
export function sortTimberProjectsForHubWorkQueue(
  timberProjects: TimberProject[],
  userId: string,
  projectTasks: ProjectTask[],
  taskGroups: TaskGroup[],
  projectOnboardings: ProjectOnboarding[],
  queueIds: string[],
  dealsById: Map<string, EnhancedDeal>,
): TimberProject[] {
  if (!userId) return [];
  const withWork = timberProjects.filter((p) =>
    projectHasActiveWorkQueueInvolvement(p, userId, projectTasks, taskGroups, projectOnboardings),
  );
  return sortProjectsByWorkQueueThenNumber(withWork, queueIds, dealsById);
}
